import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, EventType, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company/company.service';
import { UserService } from 'src/app/services/user/user.service';
import { Company, Currency } from 'src/app/models/verylys';
import { VgOffCanvasComponent } from '../vg-off-canvas/vg-off-canvas.component';
import { CurrencyAndLanguageService } from './currency-and-language.service';
import { CompanyConfig } from 'src/config';
import { formalizedItemFromUrl } from 'src/app/utilities/window';
import { languages as _languages, Language as _Language } from 'src/app/utilities/language';
import { clone } from 'lodash';

interface Language extends _Language {
    url: string;
}

enum IDisplay {
    Currency = "CURRENCY",
    CurrencySymbol = "CURRENCY_SYMBOL",
    Language = "LANGUAGE",
    Both = "BOTH"
}

@Component({
    selector: 'currency-and-language',
    templateUrl: './currency-and-language.component.html',
    styleUrls: ['./currency-and-language.component.scss']
})
export class CurrencyAndLanguageComponent implements OnInit, OnDestroy {
    @ViewChild('offcanvas') offcanvas: VgOffCanvasComponent;

    @Input('display') display: IDisplay = IDisplay.Both;
    @Input('canChange') canChange: boolean = true;

    protected currencies: Currency[] = [];
    protected currency: Currency = null;

    protected language: Language = null;
    protected languages: Language[] = [];
    protected IDisplay: typeof IDisplay = IDisplay;
    private destroy$: Subject<void> = new Subject();
    private currencyChange$: Subject<void> = new Subject();

    constructor(
        private userService: UserService,
        private currencyAndLanguageService: CurrencyAndLanguageService,
        private companyService: CompanyService,
        private router: Router) {
        this.languages = clone(_languages) as Language[];
    }

    async ngOnInit(): Promise<void> {
        this.router.events
            .pipe(takeUntil(this.destroy$))
            .subscribe((event) => {
                if (event.type == EventType.NavigationEnd) {
                    for (let language of this.languages) {
                        language.url = this.generatUrl(language)
                    };
                }
            });

        const { locale } = formalizedItemFromUrl();

        const find = this.languages.find((item) => item.locale == locale);
        this.language = find ? find : this.languages[0];

        this.loadCurrencies();

        /*this.userService.onLocalStorageCountryChange
          .pipe(takeUntil(this.destroy))
          .subscribe((value: string) => );*/

        this.userService.onLocalStorageCurrencyChange
            .pipe(takeUntil(this.destroy$))
            .subscribe((value: string) => this.currency = this.findCurrencyById(value));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        this.currencyChange$.next();
        this.currencyChange$.complete();
    }

    protected openOffCanvas(): void {
        if (!this.canChange) {
            return;
        }

        this.offcanvas?.open();
    }

    /** Load the currencies of the company */
    private async loadCurrencies(): Promise<void> {
        this.companyService.get(CompanyConfig.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((result: Company) => {
                this.currencies = result.available_currencies as Currency[];

                const userCurrency: string = this.userService.getCurrencyFromLocalStorage();
                this.currency = this.findCurrencyById(userCurrency);
            });
    }

    /* protected findCountryById(id: string): Currency {
       return this.country = this.currencies.find((currency: Currency) => currency._id == id);
     }*/

    protected findCurrencyById(id: string): Currency {
        return this.currencies.find((currency: Currency) => currency._id == id);
    }

    protected async onCurrencyChange(currency: Currency): Promise<void> {
        if (!this.canChange) {
            return;
        }

        this.currencyChange$.next();

        this.userService.changeCurrency(currency._id, false)
            .pipe(takeUntil(this.currencyChange$))
            .subscribe({
                next: () => {
                    this.currency = currency;
                    this.currencyAndLanguageService.currencyChange(currency._id);
                },
                error: () => {
                    this.currency = currency;
                    this.currencyAndLanguageService.currencyChange(currency._id);
                }
            })

        this.offcanvas.close();
    }

    private generatUrl(language: Language): string {
        // TODO: call API ROUTING to change language, don't forget to set FALSE to emitter 
        const { origin } = formalizedItemFromUrl();
        return `${origin}/${language.locale}${this.router.url}`;
    }
}
